/* Header interactions: mobile drawer + instant predictive search modal + brand filtering.
   Exposes window.initHeader() so the theme editor can re-init after section re-renders.
*/
(function () {
  'use strict';

  function initHeader() {
    // 0. Search panel shop preview (phones): tapping a category pill shows its tiles.
    document.querySelectorAll('[data-search-shop]').forEach((shop) => {
      if (shop.dataset.searchShopInitialized) return;
      shop.dataset.searchShopInitialized = 'true';
      const tabs = Array.from(shop.querySelectorAll('[data-search-shop-tab]'));
      const panes = shop.querySelectorAll('[data-search-shop-pane]');
      const show = (tab) => {
        const key = tab.getAttribute('data-search-shop-tab');
        tabs.forEach((other) => {
          const active = other === tab;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-selected', String(active));
        });
        panes.forEach((pane) => {
          pane.hidden = pane.getAttribute('data-search-shop-pane') !== key;
        });
      };
      tabs.forEach((tab) => {
        tab.addEventListener('click', () => show(tab));
      });

      // A sideways swipe on the tiles moves to the next or previous category.
      let startX = 0;
      let startY = 0;
      shop.addEventListener(
        'touchstart',
        (event) => {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        },
        { passive: true }
      );
      shop.addEventListener(
        'touchend',
        (event) => {
          const dx = event.changedTouches[0].clientX - startX;
          const dy = event.changedTouches[0].clientY - startY;
          if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
          const current = tabs.findIndex((tab) => tab.classList.contains('is-active'));
          const next = tabs[current + (dx < 0 ? 1 : -1)];
          if (next) show(next);
        },
        { passive: true }
      );
    });

    // 1. Mobile Navigation Drawer
    // Scoped ids live in the header section (nav-drawer-{{ section.id }}); the
    // plain-JS asset resolves the instance via its data hook instead.
    const drawer = document.querySelector('[data-nav-drawer]');
    const navToggle = document.querySelector('.nav-toggle');
    if (drawer && navToggle && !navToggle.dataset.headerInitialized) {
      navToggle.dataset.headerInitialized = 'true';

      const setNavOpen = (open) => {
        drawer.hidden = false;
        drawer.classList.toggle('is-open', open);
        navToggle.setAttribute('aria-expanded', String(open));
        document.body.style.overflow = open ? 'hidden' : '';
        if (!open) {
          setTimeout(() => {
            if (!drawer.classList.contains('is-open')) drawer.hidden = true;
          }, 200);
        }
      };

      navToggle.addEventListener('click', () => setNavOpen(!drawer.classList.contains('is-open')));
      drawer.querySelectorAll('[data-nav-close]').forEach((el) => {
        el.addEventListener('click', () => setNavOpen(false));
      });
    }

    // 2. Search Modal & Predictive Search with Brand Separation (NANO vs Philips)
    const searchModal = document.querySelector('[data-search-modal]');
    const searchToggles = document.querySelectorAll('[data-search-open]');
    const searchInput = searchModal ? searchModal.querySelector('[data-search-input]') : null;
    const searchClear = searchModal ? searchModal.querySelector('[data-search-clear]') : null;
    const searchLoading = searchModal ? searchModal.querySelector('[data-search-loading]') : null;
    const searchList = searchModal ? searchModal.querySelector('[data-search-list]') : null;
    const searchFooter = searchModal ? searchModal.querySelector('[data-search-footer]') : null;
    const searchViewAll = searchModal ? searchModal.querySelector('[data-search-view-all]') : null;
    const brandButtons = searchModal ? searchModal.querySelectorAll('[data-brand-filter]') : [];
    const categoryChips = searchModal ? searchModal.querySelectorAll('[data-search-chip]') : [];

    if (searchModal && !searchModal.dataset.searchInitialized) {
      searchModal.dataset.searchInitialized = 'true';

      let currentBrand = 'nano'; // Default focus on NANO brand
      let activeQuery = '';
      let debounceTimer = null;
      let cachedResults = [];

      const brandTerm = () =>
        currentBrand === 'philips' ? 'Philips' : currentBrand === 'nano' ? 'NANO' : '';

      const setSearchOpen = (open) => {
        searchModal.hidden = false;
        searchModal.classList.toggle('is-open', open);
        searchToggles.forEach(t => t.setAttribute('aria-expanded', String(open)));
        document.body.style.overflow = open ? 'hidden' : '';
        if (open) {
          setTimeout(() => {
            // Phones: leave the keyboard down so the category tiles stay in view;
            // the visitor taps the box when they want to type.
            if (searchInput && window.matchMedia('(min-width: 900px)').matches) searchInput.focus();
            if (!searchInput.value.trim()) {
              // Preload default NANO hero recommendations
              fetchSuggestions('NANO');
            }
          }, 80);
        } else {
          setTimeout(() => {
            if (!searchModal.classList.contains('is-open')) searchModal.hidden = true;
          }, 250);
        }
      };

      searchToggles.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          setSearchOpen(true);
        });
      });

      searchModal.querySelectorAll('[data-search-close]').forEach((el) => {
        el.addEventListener('click', () => setSearchOpen(false));
      });

      // Brand Filter Tabs Switching
      brandButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          brandButtons.forEach(b => {
            b.classList.remove('is-active');
            b.setAttribute('aria-selected', 'false');
          });
          btn.classList.add('is-active');
          btn.setAttribute('aria-selected', 'true');
          currentBrand = btn.getAttribute('data-brand-filter') || 'all';

          // If query is typed, filter cached; otherwise fetch brand-specific hero catalog
          if (activeQuery) {
            if (cachedResults.length > 0) {
              renderResults(cachedResults);
            } else {
              fetchSuggestions(activeQuery);
            }
          } else {
            fetchSuggestions(brandTerm());
          }
        });
      });

      // Category Chip Shortcuts
      categoryChips.forEach(chip => {
        chip.addEventListener('click', () => {
          const url = chip.getAttribute('data-search-url');
          if (url) {
            window.location.href = url;
            return;
          }
          const term = chip.getAttribute('data-search-chip');
          if (term && searchInput) {
            searchInput.value = term;
            searchInput.dispatchEvent(new Event('input'));
            searchInput.focus();
          }
        });
      });

      // Clear Button
      if (searchClear && searchInput) {
        searchClear.addEventListener('click', () => {
          searchInput.value = '';
          searchClear.hidden = true;
          searchInput.focus();
          fetchSuggestions(brandTerm());
        });
      }

      // Format Price Helper
      function formatMoney(amount) {
        if (!amount) return '';
        const num = parseFloat(amount);
        return isNaN(num) ? amount : '฿' + num.toLocaleString(document.documentElement.lang || 'th', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }

      // Render Search Items
      function renderResults(products) {
        if (!searchList) return;
        cachedResults = products;

        // Apply active brand filter
        let filtered = products;
        if (currentBrand === 'nano') {
          filtered = products.filter(p => {
            const v = (p.vendor || '').toLowerCase();
            const t = (p.title || '').toLowerCase();
            return v.includes('nano') || t.includes('nano');
          });
        } else if (currentBrand === 'philips') {
          filtered = products.filter(p => {
            const v = (p.vendor || '').toLowerCase();
            const t = (p.title || '').toLowerCase();
            return v.includes('philips') || t.includes('philips');
          });
        }

        if (filtered.length === 0) {
          const empty = document.createElement('div');
          empty.className = 'search-modal__empty';
          const emptyTitle = document.createElement('h4');
          emptyTitle.textContent = `${searchList.dataset.emptyTitle} ${currentBrand === 'nano' ? 'NANO' : (currentBrand === 'philips' ? 'Philips' : '')}`.trim();
          const emptyText = document.createElement('p');
          emptyText.textContent = searchList.dataset.emptyText;
          empty.append(emptyTitle, emptyText);
          searchList.replaceChildren(empty);
          if (searchFooter) searchFooter.hidden = true;
          return;
        }

        const itemsHtml = filtered.slice(0, 8).map(p => {
          const isNano = (p.vendor || '').toLowerCase().includes('nano') || (p.title || '').toLowerCase().includes('nano');
          const vendorLabel = isNano ? 'NANO' : 'Philips';
          const vendorClass = isNano ? 'search-item__vendor--nano' : 'search-item__vendor--philips';
          const imgUrl = p.image || (p.featured_image ? p.featured_image.url : '');
          const price = formatMoney(p.price);
          const comparePrice = p.compare_at_price_min && parseFloat(p.compare_at_price_min) > parseFloat(p.price) ? formatMoney(p.compare_at_price_min) : '';

          return `
            <a href="${p.url}" class="search-item">
              <div class="search-item__media">
                ${imgUrl ? `<img src="${imgUrl}" alt="${p.title}" loading="lazy" width="56" height="56">` : ''}
              </div>
              <div class="search-item__info">
                <div class="search-item__meta">
                  <span class="search-item__vendor ${vendorClass}">${vendorLabel}</span>
                  ${p.type ? `<span class="search-item__type">${p.type}</span>` : ''}
                </div>
                <h4 class="search-item__title">${p.title}</h4>
              </div>
              <div class="search-item__pricing">
                <span class="search-item__price">${price}</span>
                ${comparePrice ? `<span class="search-item__compare">${comparePrice}</span>` : ''}
              </div>
            </a>
          `;
        }).join('');

        searchList.innerHTML = itemsHtml;

        if (searchFooter && searchViewAll) {
          searchFooter.hidden = false;
          searchViewAll.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(activeQuery || brandTerm())}&options%5Bprefix%5D=last`;
        }
      }

      // Fetch from Shopify Predictive Search API
      async function fetchSuggestions(term) {
        const queryTerm = (term !== undefined ? term : (searchInput ? searchInput.value : '')).trim();
        if (searchLoading) searchLoading.hidden = false;
        if (searchList) searchList.style.opacity = '0.5';

        try {
          const effectiveTerm = queryTerm || brandTerm();
          const query = encodeURIComponent(effectiveTerm);
          const res = await fetch(`${window.Shopify.routes.root}search/suggest.json?q=${query}&resources[type]=product&resources[limit]=10`);
          if (!res.ok) throw new Error('Search request failed');
          const data = await res.json();
          const products = (data.resources && data.resources.results && data.resources.results.products) || [];
          renderResults(products);
        } catch (err) {
          console.error('Search error:', err);
        } finally {
          if (searchLoading) searchLoading.hidden = true;
          if (searchList) searchList.style.opacity = '1';
        }
      }

      // Input Event with Debouncing
      if (searchInput) {
        searchInput.addEventListener('input', () => {
          const val = searchInput.value;
          if (searchClear) searchClear.hidden = !val;
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            fetchSuggestions(val);
          }, 180);
        });
      }
    }

    // 3. Global Escape Key Listener
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (drawer && drawer.classList.contains('is-open')) {
          drawer.classList.remove('is-open');
          drawer.hidden = true;
          document.body.style.overflow = '';
        }
        if (searchModal && searchModal.classList.contains('is-open')) {
          searchModal.classList.remove('is-open');
          searchModal.hidden = true;
          document.body.style.overflow = '';
        }
      }
    });

    // 4. Dynamic Scroll Header State
    const header = document.querySelector('.site-header');
    if (header && !header.dataset.scrollInitialized) {
      header.dataset.scrollInitialized = 'true';
      let ticking = false;
      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            const isScrolled = window.scrollY > 20;
            header.classList.toggle('is-scrolled', isScrolled);
            ticking = false;
          });
          ticking = true;
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    // 5. Mega shop tabs (ช้อป panel): pure-CSS :has() hover previews the
    // pointed category, but the pointer can't travel pill → product without
    // leaving the pill (the pane hides mid-cross). Class-based activation on
    // pointerenter/focus keeps the pane — and its product links — reachable;
    // leaving the panel resets to the first category.
    document.querySelectorAll('[data-mega-shop]').forEach((shop) => {
      if (shop.dataset.megaShopInitialized) return;
      shop.dataset.megaShopInitialized = 'true';
      const pills = Array.from(shop.querySelectorAll('.mega-tab__pill'));
      const panes = Array.from(shop.querySelectorAll('.mega-tab__pane'));
      if (!pills.length || pills.length !== panes.length) return;
      shop.classList.add('mega-shop--js');
      const setActive = (index) => {
        pills.forEach((pill, i) => pill.classList.toggle('is-active', i === index));
        panes.forEach((pane, i) => pane.classList.toggle('is-active', i === index));
      };
      pills.forEach((pill, i) => {
        pill.addEventListener('pointerenter', () => setActive(i));
        pill.addEventListener('focus', () => setActive(i));
      });
      shop.addEventListener('pointerleave', () => setActive(0));
    });

    // 6. Category nav panels (touch): first tap opens the dropdown instead of
    // navigating, second tap follows the link. Hover devices keep pure CSS.
    document.querySelectorAll('.site-nav__item--panel > .site-nav__link--panel').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!window.matchMedia('(hover: none)').matches) return;
        const item = link.closest('.site-nav__item--panel');
        if (!item.classList.contains('is-touch-open')) {
          event.preventDefault();
          item.classList.add('is-touch-open');
        }
      });
    });
    document.addEventListener('click', (event) => {
      document
        .querySelectorAll('.site-nav__item--panel.is-touch-open')
        .forEach((item) => {
          if (!item.contains(event.target)) item.classList.remove('is-touch-open');
        });
    });
  }

  window.initHeader = initHeader;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeader);
  } else {
    initHeader();
  }
})();

